import { Control, UseFormGetValues } from "react-hook-form/dist/types/form";
import { LaptopRenewalFormValue } from "../../../LaptopReplacementModal";
import { Box, MenuItem, Typography } from "@mui/material";
import { Controller } from "react-hook-form";
import { useContext, useEffect, useMemo } from "react";
import {
  DeliveryModeEnum,
  StringFulBooleanValue,
} from "../../../../../helper/constant";
import {
  AssetWithState,
  ReplacementUserContext,
} from "../../../../AssetList/AssetList";
import CustomerSelect from "../../CustomerSelect/CustomerSelect";
import { truncateTitle } from "../../../../../helper/helper";

type Props = {
  getValues: UseFormGetValues<LaptopRenewalFormValue>;
  control: Control<LaptopRenewalFormValue, any>;
  currentAsset: AssetWithState;
};

const formTitleStyles = {
  marginBottom: "12px",
  fontFamily: "Inter",
  fontWeight: "600",
  fontSize: "16px",
};

const formSubTitleStyles = {
  marginBottom: "24px",
  fontFamily: "Inter",
  fontWeight: "400",
  fontSize: "16px",
};

export type FormOption = {
  city: string;
  country: string;
  stateISOCode?: string;
  postalCode: string;
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  isPrimary?: string;
  addressUsage?: string[];
  state: string;
  countryISOCode?: string;
  fullAddress?: string;
  mailingAddress?: string;
  optionTitle: string;
};

const UserDetailForm = ({ getValues, control, currentAsset }: Props) => {
  const userHomeDelivery = useMemo(
    () =>
      Boolean(
        currentAsset &&
          currentAsset.deliveryMode != DeliveryModeEnum.HOME_DELIVERY
      ),
    [currentAsset]
  );

  const {
    userContactInfoFromWD,
    isContactInfoLoading,
    isContactInfoError,
    isUserLocationsError,
    userOfficeLocationFromSN,
    isUserOfficeLocationsLoading,
  } = useContext(ReplacementUserContext);

  const isUserLocationSelectLoading = useMemo(() => {
    return currentAsset.deliveryMode === DeliveryModeEnum.HOME_DELIVERY
      ? isContactInfoLoading
      : isContactInfoLoading || isUserOfficeLocationsLoading;
  }, [
    currentAsset.deliveryMode,
    isContactInfoLoading,
    isUserOfficeLocationsLoading,
  ]);

  const isDeliveryLocationValid = useMemo(
    () =>
      currentAsset.deliveryMode === DeliveryModeEnum.COLLECT_AT_OFFICE
        ? !isUserLocationsError
        : currentAsset.deliveryMode === DeliveryModeEnum.BOTH
        ? !isContactInfoError && !isUserLocationsError
        : !isContactInfoError,
    [currentAsset, isUserLocationsError, isContactInfoError]
  );

  const deliveryLocationOption = useMemo(() => {
    if (isUserLocationSelectLoading) return null;

    const { deliveryMode, isAllCountryLocationShowed } = currentAsset;

    //deliveryMode is HOME_DELIVERY, check is userContactInfoFromWD has data
    if (deliveryMode === DeliveryModeEnum.HOME_DELIVERY) {
      if (userContactInfoFromWD) {
        const { homeAddress } = userContactInfoFromWD;
        return homeAddress as FormOption[];
      }
      return null;
    }

    // deliveryMode is COLLECT_AT_OFFICE, check is userContactInfoFromWD has data
    if (deliveryMode === DeliveryModeEnum.COLLECT_AT_OFFICE) {
      if (userOfficeLocationFromSN) {
        const { homeLocations, workLocations, allHomeOfficeLocations } =
          userOfficeLocationFromSN;
        const locations = [...homeLocations, ...workLocations] as FormOption[];
        return isAllCountryLocationShowed === StringFulBooleanValue.FALSE
          ? locations
          : locations.concat(allHomeOfficeLocations);
      }
      return null;
    }

    // deliveryMode is ALL, check is userContactInfoFromWD and userContactInfoFromWD both has data
    if (deliveryMode === DeliveryModeEnum.BOTH) {
      if (userOfficeLocationFromSN && userContactInfoFromWD) {
        const { homeAddress } = userContactInfoFromWD;
        const { homeLocations, workLocations, allHomeOfficeLocations } =
          userOfficeLocationFromSN;
        const locations = [...homeLocations, ...workLocations] as FormOption[];
        const officeLocations =
          isAllCountryLocationShowed === StringFulBooleanValue.FALSE
            ? locations
            : locations.concat(allHomeOfficeLocations);
        return homeAddress.concat(officeLocations);
      }
    }
  }, [
    isUserLocationSelectLoading,
    currentAsset.deliveryMode,
    currentAsset.isAllCountryLocationShowed,
    userContactInfoFromWD,
    userOfficeLocationFromSN,
  ]);

  const userContactNumber = useMemo(() => {
    if (isContactInfoLoading || isContactInfoError) return null;
    return userContactInfoFromWD.contactInformation;
  }, [userContactInfoFromWD]);

  const findLocationByAddress = (locations: FormOption[], value: string) => {
    return locations.find((location) => location.mailingAddress === value);
  };

  return (
    <Box>
      <Box id={"mail-address-item"} sx={{ marginBottom: "40px" }}>
        <Typography variant="h6" component="h2" sx={formTitleStyles}>
          Mailing Address
        </Typography>
        <Typography component="div" sx={formSubTitleStyles}>
          Where do you prefer your laptop to be delivered?
        </Typography>
        <Controller
          rules={{
            required: true,
          }}
          name="deliveryLocation"
          control={control}
          render={({ field }) => (
            <CustomerSelect
              {...field}
              value={getValues("deliveryLocation")?.mailingAddress}
              variant={"standard"}
              sx={{ width: "100%" }}
              inputLabel="Select delivery location"
              isLoading={isUserLocationSelectLoading}
              isError={!isDeliveryLocationValid}
              isEmpty={Boolean(
                deliveryLocationOption && deliveryLocationOption.length === 0
              )}
              onChange={(e) => {
                field.onChange(
                  findLocationByAddress(
                    deliveryLocationOption,
                    e.target.value as string
                  )
                );
              }}
            >
              {deliveryLocationOption?.map((loc) => (
                <MenuItem
                  value={loc.mailingAddress}
                  key={
                    loc.mailingAddress + currentAsset?.details?.mailingAddress
                  }
                  selected={Boolean(
                    loc.mailingAddress === currentAsset?.details?.mailingAddress
                  )}
                  sx={
                    loc.optionTitle.includes("Home Address") ||
                    loc.optionTitle.includes("Working Office")
                      ? { borderBottom: "1px solid #0000001F" }
                      : {}
                  }
                >
                  {truncateTitle(loc.optionTitle)}
                </MenuItem>
              ))}
            </CustomerSelect>
          )}
        />
      </Box>
      <Box id={"contact-number-item"} sx={{ marginBottom: "40px" }}>
        <Typography variant="h6" component="h2" sx={formTitleStyles}>
          Contact Number
        </Typography>
        <Typography component="div" sx={formSubTitleStyles}>
          Please select your preferred contact number
        </Typography>
        <Controller
          rules={{
            required: true,
          }}
          name="contactNumber"
          control={control}
          render={({ field }) => (
            <CustomerSelect
              {...field}
              inputLabel="Select contact number"
              value={getValues("contactNumber")}
              variant={"standard"}
              sx={{ width: "100%" }}
              isLoading={isContactInfoLoading}
              isError={Boolean(isContactInfoError)}
              isEmpty={Boolean(
                userContactNumber && userContactNumber.length === 0
              )}
            >
              {userContactNumber?.map((number) => (
                <MenuItem value={number} key={number}>
                  {number}
                </MenuItem>
              ))}
            </CustomerSelect>
          )}
        />
      </Box>
    </Box>
  );
};

export default UserDetailForm;
